import React, { useEffect, useState } from 'react';
import OperatorLogo from './OperatorLogo';
import { X, ArrowRight } from 'lucide-react';

export default function OperatorPlansModal({ isOpen, onClose, operator, operatorName, plans, onSelectPlan, initialPlanType }) {
  const selectedOperator = operator || operatorName;
  const [selectedPlanType, setSelectedPlanType] = useState(initialPlanType || null);

  useEffect(() => {
    if (isOpen) setSelectedPlanType(initialPlanType || null);
  }, [isOpen, selectedOperator, initialPlanType]);

  if (!isOpen || !selectedOperator) return null;

  const filteredPlans = plans.filter(p =>
    (String(selectedOperator).trim().toLowerCase() === 'top sim cards' ||
      String(p.operator || '').trim().toLowerCase() === String(selectedOperator).trim().toLowerCase()) &&
    (selectedPlanType === 'all' || String(p.plan_type || '').trim().toLowerCase() === selectedPlanType)
  );

  const getPlanBenefits = (description) =>
    String(description || '')
      .split(/\r?\n/)
      .map(benefit => benefit.trim())
      .filter(Boolean);

  return (
    <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-md flex items-center justify-center z-[9990] p-4 animate-in fade-in duration-200">
      <div className="glass-modal max-w-3xl w-full rounded-3xl max-h-[85vh] overflow-y-auto p-6 sm:p-8 shadow-2xl border border-white/80">
        
        {/* Header */}
        <div className="flex justify-between items-center border-b border-slate-200/60 pb-4 mb-6">
          <div className="flex items-center gap-3">
            <OperatorLogo operator={selectedOperator} size="md" />
            <div>
              <h2 className="text-xl font-black text-slate-900">{selectedOperator} SIM Plans</h2>
              <p className="text-xs text-slate-500">Free Doorstep delivery with 100% paperless e-KYC</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-500 transition">
            <X className="w-5 h-5" />
          </button>
        </div>

        {!selectedPlanType ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-6">
            {[['prepaid', 'Prepaid SIMs'], ['postpaid', 'Postpaid SIMs']].map(([type, label]) => (
              <button
                key={type}
                onClick={() => setSelectedPlanType(type)}
                className="rounded-2xl border border-slate-200 bg-white/80 p-6 text-left shadow-sm transition hover:-translate-y-1 hover:border-orange-300 hover:shadow-md"
              >
                <span className="block text-lg font-black text-slate-900">{label}</span>
                <span className="mt-1 flex items-center gap-1 text-xs font-bold text-orange-600">
                  View plans <ArrowRight className="h-4 w-4" />
                </span>
              </button>
            ))}
          </div>
        ) : filteredPlans.length === 0 ? (
          <p className="py-12 text-center text-sm text-slate-500">No active {selectedPlanType === 'all' ? '' : selectedPlanType} plans available for {selectedOperator} right now.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {filteredPlans.map(plan => (
              <div 
                key={plan.id} 
                className="bg-white/80 rounded-2xl p-5 border border-slate-200/80 shadow-sm hover:shadow-md transition-all flex flex-col justify-between"
              >
                <div>
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="font-bold text-slate-900 text-base">{plan.name}</h3>
                    <span className="text-[10px] font-extrabold px-2 py-0.5 rounded-full bg-orange-50 text-orange-700 border border-orange-200">
                      Popular
                    </span>
                  </div>
                  <div className="flex items-baseline gap-1 mb-2">
                    <span className="text-3xl font-black text-slate-900">₹{plan.price}</span>
                    <span className="text-xs font-semibold text-slate-500">/ {plan.validity_days} Days</span>
                  </div>
                  {getPlanBenefits(plan.description).length > 0 && (
                    <ul className="mb-4 list-disc space-y-1 pl-4 text-xs text-slate-600">
                      {getPlanBenefits(plan.description).map((benefit, index) => (
                        <li key={`${plan.id}-benefit-${index}`}>{benefit}</li>
                      ))}
                    </ul>
                  )}
                </div>

                <button
                  onClick={() => { onSelectPlan(plan); onClose(); }}
                  className="w-full py-2.5 rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-bold text-xs shadow-sm flex items-center justify-center gap-1.5 transition"
                >
                  Select Plan <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}